import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from '@rocco/components/accordion';

import type { AccordionSection, B2BAssets } from '../types';
import { B2BCTAButton } from '../components/B2BCTAButton';

export interface B2BPillarAccordionProps {
    section: AccordionSection;
    assets: B2BAssets;
}

/**
 * Expand/collapse accordion used by both the platform pillars
 * (Figma `443:24130`, 1920×1241) and the Business Manager tech accordion
 * (Figma `480:26401`, 1920×1311). Both share the same coordinate system; the
 * Tech frame is 70 px taller because the open body copy is longer.
 *
 * **Standard fixed-position accordion.** Items render in their original
 * config order. Clicking a closed row expands its body INLINE below the
 * row's title; the previously open row collapses inline. The row positions
 * don't reorder — items keep their relative vertical order, only the body
 * area grows / shrinks via Radix's height animation. (An earlier
 * "bubble-to-top" pattern was replaced after user feedback that the jumping
 * felt like a reset.)
 *
 * Built on Rocco's Radix-based `<Accordion>` primitives so each item carries
 * the canonical Radix `data-state="open" | "closed"` attribute. The whole
 * accordion stack sits absolute-positioned at the spec start (y=371.17,
 * x=25, w=862.745); items inside flow naturally with a flex column.
 *
 * Layout — absolute-positioned 1920 frame:
 *  - Eyebrow `(Platform)` / `(ArchiPro Business Manager)` at x=25, y=23.89
 *    (28 px Neue Haas Roman, tracking −1.232 px).
 *  - 80 px title at x=25, two-line w=928 (pillars) / 990 (tech), indent 193 px
 *    on the first line, tracking −3.84 px, leading 1.07.
 *  - Accordion stack starts at y=371.17, x=25, w=862.745.
 *    - Each row: 28 px Roman title at x=0 (left), optional descriptor at
 *      x=265.2 (only Products when open), plus icon at x=823.85 (right).
 *    - Open body: 25 px Medium #888 at x=265.2 below the title, w=535.99
 *      (pillars) or 597.54 (tech). Slides via Radix accordion-down/up.
 *    - 1 px #181818 border below each row.
 *  - CTA pill at (21.41, 1146.43) when provided.
 *  - Supporting image at x=1043.98 / y=0 / w=851.02 / h=full-frame on the right.
 *
 * vw conversions assume the 1920 design frame: px / 19.2 = vw.
 */

/** Plus icon — Figma node `443:24328`, 17.152×17.152 px, thin 1.5 stroke "+". */
const PlusIcon = () => (
    <svg
        viewBox="0 0 18 18"
        aria-hidden="true"
        className="size-full transition-transform duration-300 ease-out group-data-[state=open]:rotate-45"
    >
        <line x1="9" y1="1" x2="9" y2="17" stroke="#181818" strokeWidth="1.5" />
        <line x1="1" y1="9" x2="17" y2="9" stroke="#181818" strokeWidth="1.5" />
    </svg>
);

/** Accordion stack spec coords (1920 frame). */
const STACK_TOP_Y = 371.17;
const STACK_LEFT_X = 25;
const STACK_WIDTH_X = 862.745;
/** Within each row, the descriptor + body indent (x=290.2 - row x=25). */
const ROW_BODY_INDENT_PX = 265.2;
/** Within each row, the plus-icon X offset (x=848.85 - row x=25). */
const ROW_PLUS_X = 823.85;
const PLUS_ICON_PX = 17.152;

export const B2BPillarAccordion = ({
    section,
    assets,
}: B2BPillarAccordionProps) => {
    const sectionImageSrc = section.imageAssetKey
        ? assets[section.imageAssetKey]
        : undefined;
    // Per-item image with section-level fallback. The active (expanded) item's
    // image is shown; changing the active item crossfades the right-hand image.
    const resolveItemImage = (
        item: AccordionSection['items'][number],
    ): string | undefined =>
        (item.imageAssetKey ? assets[item.imageAssetKey] : undefined) ??
        sectionImageSrc;
    const hasAnyImage = section.items.some((item) => resolveItemImage(item));
    const defaultOpenId = section.defaultOpenId ?? section.items[0]?.id;
    // Controlled Radix root so we can recompute slot assignments when the
    // open item changes. `collapsible` lets the user close the open item via
    // Radix's own keyboard handling, but our slot-assignment falls back to
    // the default-open id when nothing is open so the top slot is never empty.
    const [openId, setOpenId] = useState<string>(defaultOpenId ?? '');

    const frameHeight = section.frameHeight ?? 1241;
    const titleWidthPx = section.titleWidthPx ?? 928;
    const sectionBgClass = section.sectionBgClass ?? 'bg-white';
    const titleIndentPx = section.titleIndentPx ?? 193;

    // Helpers: px → percent of 1920×frameHeight frame.
    const xPct = (px: number) => `${(px / 1920) * 100}%`;
    const yPct = (px: number) => `${(px / frameHeight) * 100}%`;
    const wPct = (px: number) => `${(px / 1920) * 100}%`;

    // Falls back to the default-open id if the user collapsed the open row
    // (Radix `collapsible`).
    const effectiveOpenId =
        section.items.find((it) => it.id === openId)?.id ?? defaultOpenId ?? '';

    return (
        <section
            className={cn(
                // `aspect-auto!` beats the inline `aspectRatio` style — the
                // section flows freely on mobile while desktop keeps the
                // fixed Figma frame proportion. Mobile padding is just the
                // 5.4 % side gutter (used everywhere else) — the parent
                // `B2BLandingPage` gap-[14.84vw] provides inter-section
                // breathing room, so the section itself doesn't need py.
                'relative w-full text-[#181818] max-md:aspect-auto! max-md:px-[5.4%]',
                sectionBgClass,
            )}
            style={{ aspectRatio: `1920 / ${frameHeight}` }}
        >
            {/* Right-column supporting image — desktop only.
             *  Figma mobile `521:26864` omits this image; the accordion stands
             *  on its own with the eyebrow + title above. */}
            {hasAnyImage && (
                <div
                    className="absolute overflow-hidden max-md:hidden"
                    style={{
                        left: xPct(1043.98),
                        top: 0,
                        width: wPct(851.02),
                        height: '100%',
                    }}
                >
                    {/* All item images are stacked; the active (expanded) item's
                     *  image fades in while the rest fade out, so opening a
                     *  different accordion item crossfades the supporting image.
                     *  Respects prefers-reduced-motion. */}
                    {section.items.map((item) => {
                        const src = resolveItemImage(item);
                        if (!src) {
                            return null;
                        }
                        const isActive = item.id === effectiveOpenId;
                        return (
                            <img
                                key={item.id}
                                src={src}
                                alt=""
                                aria-hidden={!isActive}
                                className={cn(
                                    'absolute inset-0 size-full object-cover transition-opacity duration-500 ease-out motion-reduce:transition-none',
                                    isActive ? 'opacity-100' : 'opacity-0',
                                )}
                            />
                        );
                    })}
                </div>
            )}

            {/* Eyebrow.
             *  - Desktop: x=25, y=23.89, 28 px Roman, absolute-positioned.
             *  - Mobile: stacked above the title for readability (the Figma
             *    inline-in-text-indent pattern compresses awkwardly at 414
             *    viewport — eyebrow + title both indent slightly into the
             *    same column for a cleaner stacked look). Uses
             *    `mobileEyebrow` if provided, falls back to `eyebrow`. */}
            {section.eyebrow && (
                <p
                    className="absolute whitespace-nowrap font-sans text-[1.458vw] leading-[1.07] tracking-[-0.044em] text-[#181818] max-md:hidden"
                    style={{ left: xPct(25), top: yPct(23.89) }}
                >
                    {section.eyebrow}
                </p>
            )}
            {(section.mobileEyebrow ?? section.eyebrow) && (
                <p className="mb-[3vw] hidden whitespace-nowrap font-sans text-[3.255vw] leading-[1.07] tracking-[-0.044em] text-[#181818] max-md:block">
                    {section.mobileEyebrow ?? section.eyebrow}
                </p>
            )}

            {/* Title.
             *  - Desktop: x=25, y=0, 80 px Roman, first-line indent. */}
            <h2
                className="absolute font-sans text-[4.167vw] leading-[1.07] tracking-[-0.048em] text-[#181818] max-md:relative max-md:left-0! max-md:top-0! max-md:mb-[6vw] max-md:w-full! max-md:text-[6.5vw]! max-md:indent-0!"
                style={{
                    left: xPct(25),
                    top: 0,
                    width: wPct(titleWidthPx),
                    textIndent: `${titleIndentPx / 19.2}vw`,
                }}
            >
                {section.title}
            </h2>

            {/* Accordion stack — absolute-positioned at the spec start on
             *  desktop; mobile collapses to a static block under the title.
             *  Inline left/top/width overridden via `!` utilities. */}
            <div
                className="absolute max-md:static max-md:left-auto! max-md:top-auto! max-md:mt-4 max-md:w-full!"
                style={{
                    left: xPct(STACK_LEFT_X),
                    top: yPct(STACK_TOP_Y),
                    width: wPct(STACK_WIDTH_X),
                }}
            >
                <div
                    aria-hidden
                    className="absolute -top-px left-0 right-0 h-px bg-[#181818] max-md:hidden"
                />
                <Accordion
                    type="single"
                    collapsible
                    value={effectiveOpenId}
                    onValueChange={(v) => setOpenId(v || effectiveOpenId)}
                    className="flex flex-col"
                >
                    {section.items.map((item) => (
                        <AccordionItem
                            key={item.id}
                            value={item.id}
                            variant="noBorder"
                            className="relative border-b border-[#181818] max-md:border-[#181818]/30"
                        >
                            <AccordionTrigger
                                variant="narrow"
                                // Mobile: 2-column grid pins descriptor + body
                                // to the same `ROW_BODY_INDENT_PX` left edge.
                                // Column 1 is fixed at 30.74 % so the title
                                // ("Products") occupies the narrow gutter and
                                // the descriptor / plus icon line up with the
                                // body column below (Figma 521:26864).
                                // `items-center` vertically centers the row
                                // title against the descriptor / plus icon
                                // so they read as one row.
                                className="group relative block! py-[1.563vw]! text-left! max-md:grid! max-md:grid-cols-[30.74%_1fr]! max-md:items-center max-md:py-[3vw]! [&>svg]:hidden!"
                            >
                                {/* Title left — Figma 521:26864 row title:
                                 *  43.95/768=5.72% left, 28 px font = 3.65vw,
                                 *  Medium weight. */}
                                <span className="font-sans text-[1.458vw] font-medium leading-[1.07] tracking-[-0.044em] text-[#181818] max-md:text-[3.65vw]">
                                    {item.title}
                                </span>

                                {/* Descriptor — open row only.
                                 *  - Desktop: absolute at x=ROW_BODY_INDENT_PX
                                 *    (≈ 30.74 %), 28 px Roman.
                                 *  - Mobile (Figma `521:26864`): stays
                                 *    absolute at the SAME left (30.74 %) so it
                                 *    column-aligns with the body below, and
                                 *    is width-constrained to bodyWidthPx so
                                 *    long descriptors wrap inside the body
                                 *    column. Without this the descriptor
                                 *    ran out to the right edge while the body
                                 *    wrapped narrowly — a visible mis-align. */}
                                {item.descriptor &&
                                    item.id === effectiveOpenId && (
                                        <span
                                            // Mobile: just a grid column-2
                                            // child, no width needed (grid
                                            // template handles it).
                                            className="absolute whitespace-nowrap font-sans text-[1.458vw] font-medium leading-[1.07] tracking-[-0.044em] text-[#181818] max-md:static max-md:whitespace-normal! max-md:text-[3.65vw]!"
                                            style={{
                                                left: `${(ROW_BODY_INDENT_PX / STACK_WIDTH_X) * 100}%`,
                                                top: '1.563vw',
                                            }}
                                        >
                                            {item.descriptor}
                                        </span>
                                    )}

                                {/* Plus icon (Figma `443:24328`, 17.152 px) —
                                    rotates 45° → × on open via the keyframe
                                    inside `<PlusIcon>`. Hidden on the open
                                    row per Figma 521:26864 (the open row
                                    shows title + descriptor only, no toggle
                                    indicator). */}
                                {item.id !== effectiveOpenId && (
                                    <span
                                        aria-hidden
                                        className="absolute flex items-center justify-center max-md:static max-md:ml-auto max-md:mt-0 max-md:size-[3.65vw]! max-md:transform-none!"
                                        style={{
                                            left: `${(ROW_PLUS_X / STACK_WIDTH_X) * 100}%`,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: `${PLUS_ICON_PX / 19.2}vw`,
                                            height: `${PLUS_ICON_PX / 19.2}vw`,
                                        }}
                                    >
                                        <PlusIcon />
                                    </span>
                                )}
                            </AccordionTrigger>

                            {/* Body content. Collapses to height 0 when
                                closed via Radix's
                                `--radix-accordion-content-height` var
                                driving the `accordion-down` / `accordion-up`
                                keyframes (from `packages/rocco/tailwind.config.js`). */}
                            <AccordionContent
                                // Pull the entire content block up so the
                                // body's first line top-aligns with the
                                // trigger row's title. Each viewport has its
                                // own trigger padding:
                                //  - Desktop trigger `py-[1.563vw]` (60 px at
                                //    1920) → pull -3.126vw without descriptor,
                                //    -1.563vw with (descriptor occupies the
                                //    top slot, body lands below it).
                                //  - Mobile trigger `py-[3vw]` + items-center,
                                //    title top at ~3vw from trigger top
                                //    (~12 px at 414) → pull -6vw without
                                //    descriptor to land body at title top;
                                //    -1.5vw with descriptor so body falls
                                //    just below the descriptor line.
                                className={cn(
                                    'relative overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
                                    item.descriptor
                                        ? 'max-md:top-[-3.4vw] md:top-[-1.563vw]'
                                        : 'max-md:top-[-7.9vw] md:top-[-3.126vw]',
                                )}
                            >
                                <div
                                    className="pb-[1.563vw] max-md:pt-[1vw]"
                                    style={{
                                        paddingLeft: `${(ROW_BODY_INDENT_PX / STACK_WIDTH_X) * 100}%`,
                                    }}
                                >
                                    <p
                                        // Mobile body: 25 px = 3.255 vw,
                                        // leading 1.2 to read with breathing
                                        // room (matches the intro-band fix).
                                        className="pb-0! font-sans text-[1.302vw] font-medium leading-[1.1] tracking-[-0.03em] text-[#888] max-md:text-[3.255vw] max-md:leading-[1.2]"
                                    >
                                        {item.body
                                            .split(/\n\n+/)
                                            .map(
                                                (
                                                    para,
                                                    paraIndex,
                                                    paragraphs,
                                                ) => (
                                                    <span
                                                        key={paraIndex}
                                                        className={cn(
                                                            'block',
                                                            // First paragraph
                                                            // reads as the
                                                            // lead — render in
                                                            // body black.
                                                            paraIndex === 0 &&
                                                                'text-[#181818]',
                                                            paraIndex <
                                                                paragraphs.length -
                                                                    1 &&
                                                                'mb-[1.875vw] max-md:mb-[4vw]',
                                                        )}
                                                    >
                                                        {para}
                                                    </span>
                                                ),
                                            )}
                                    </p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>

                {/* CTA pill — Figma `505:24961`. Same spec as the About intro
                 *  band: bg #333, fully rounded, drop-shadow, Neue Haas Bold
                 *  22.703 px white. Sits naturally below the accordion stack
                 *  so the gap follows the actual content height rather than
                 *  the (overly tall) Figma frame's fixed 89.5 % offset.
                 *  Desktop margin = 3.13 vw (~60 px at 1920); mobile 6 vw. */}
                {section.ctaLabel && (
                    <div className="mt-[3.13vw] max-md:mt-[6vw]">
                        <B2BCTAButton
                            label={section.ctaLabel}
                            color="dark"
                            hideArrow
                            className="inline-flex h-[3.125vw] w-[11.99vw] items-center justify-center whitespace-nowrap rounded-full bg-[#333] px-[1.436vw] py-[0.591vw] text-[1.183vw] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)] max-md:h-auto max-md:w-auto max-md:px-6 max-md:py-3 max-md:text-base"
                        />
                    </div>
                )}
            </div>
        </section>
    );
};
